import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Box,
  IconButton,
  Paper,
  Alert,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  Add,
  Close,
  Edit,
  Delete,
  Visibility,
  Settings,
  ArrowBack,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useContractContext } from "@/contexts/ContractContext";
import {
  usePrescriptionTemplates,
  PrescriptionTemplate,
  type VetProtocolQuestionnaireItem,
} from "@/fluxvet/hooks/usePrescriptionTemplates";
import { GenericTable } from "@/components/GenericTable/GenericTable";
import { TableColumn } from "@/components/GenericTable/types";
import { TemplateEditor } from "./TemplateEditor";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/pt-br";

// Configura dayjs
dayjs.extend(relativeTime);
dayjs.locale("pt-br");

// =============================================================================
// TIPOS
// =============================================================================

interface TemplateManagerProps {
  open?: boolean;
  onClose?: () => void;
  /**
   * Quando true, renderiza o conteúdo diretamente (Box/Paper) em vez de Dialog.
   * Usado na página "Gerenciar protocolos" fora do atendimento.
   */
  asPage?: boolean;
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

/**
 * Gerenciador completo de protocolos de prescrição
 * Usa GenericTable para listagem eficiente e escalável
 */
export const TemplateManager: React.FC<TemplateManagerProps> = ({
  open = true,
  onClose = () => {},
  asPage = false,
}) => {
  const {
    templates,
    updateTemplate,
    deleteTemplate,
    getTemplateStats,
    isLoading,
  } = usePrescriptionTemplates();

  const [editingTemplate, setEditingTemplate] =
    useState<PrescriptionTemplate | null>(null);
  const [viewingTemplate, setViewingTemplate] =
    useState<PrescriptionTemplate | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [templateToDelete, setTemplateToDelete] =
    useState<PrescriptionTemplate | null>(null);

  const navigate = useNavigate();
  const { currentContract } = useContractContext();

  /**
   * Inicia edição de um protocolo
   */
  const handleStartEdit = (template: PrescriptionTemplate) => {
    setEditingTemplate(template);
    setViewingTemplate(null); // Fecha visualização ao editar
  };

  /**
   * Salva alterações do protocolo
   */
  const handleSaveEdit = async (
    id: string,
    updates: {
      name: string;
      description?: string;
      treatments?: any[];
      examsOrProcedures?: any[];
      reports?: any[];
      guidelines?: string;
      questionnaires?: VetProtocolQuestionnaireItem[];
    },
  ) => {
    try {
      await updateTemplate(id, updates);
      setEditingTemplate(null);
    } catch (error) {
      console.error("Erro ao atualizar protocolo:", error);
      // O erro já é tratado no hook, aqui só garantimos que o modal não fecha
    }
  };

  /**
   * Abre o dialog de confirmação de exclusão
   */
  const handleOpenDeleteDialog = (template: PrescriptionTemplate) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  /**
   * Fecha o dialog de confirmação de exclusão
   */
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setTemplateToDelete(null);
  };

  /**
   * Confirma e exclui o protocolo
   */
  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;

    try {
      await deleteTemplate(templateToDelete.id);
      if (viewingTemplate?.id === templateToDelete.id) {
        setViewingTemplate(null);
      }
      handleCloseDeleteDialog();
    } catch (error) {
      console.error("Erro ao excluir protocolo:", error);
      // O erro já é tratado no hook
    }
  };

  /**
   * Visualiza detalhes de um protocolo
   */
  const handleView = (template: PrescriptionTemplate) => {
    setViewingTemplate(template);
    setEditingTemplate(null); // Fecha edição ao visualizar
  };

  /**
   * Define as colunas da tabela
   */
  const tableColumns: TableColumn[] = [
    {
      column: "Nome",
      rowName: "name",
      width: "30%",
      align: "left",
    },
    {
      column: "Descrição",
      rowName: "description",
      width: "35%",
      align: "left",
    },
    {
      column: "Itens",
      rowName: "stats",
      width: "20%",
      align: "center",
    },
    {
      column: "Criado",
      rowName: "createdAt",
      width: "15%",
      align: "center",
    },
  ];

  /**
   * Prepara os dados da tabela com formatação
   */
  const tableRows = useMemo(() => {
    return templates.map((template) => {
      return {
        ...template,
        description: template.description || "-",
        stats: getTemplateStats(template).totalItems,
        createdAt: dayjs(template.createdAt).format("DD/MM/YY"),
      };
    });
  }, [templates, getTemplateStats]);

  const header = (
    <Stack
      direction="row"
      alignItems="center"
      width="100%"
      justifyContent="space-between"
      sx={asPage ? { mb: 2 } : undefined}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Settings sx={{ color: "#00A884" }} />
        <Typography variant="h6" fontWeight={600}>
          Gerenciar Protocolos de Prescrição
        </Typography>
      </Stack>
      {asPage ? (
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => navigate("/createprotocol")}
            // disabled={!currentContract}
          >
            Novo protocolo
          </Button>
          <Button
            size="small"
            startIcon={<ArrowBack />}
            onClick={onClose}
            sx={{
              padding: "10px 20px",
              textTransform: "none",
              color: "#666",
              "&:hover": {
                color: "#00A884",
                bgcolor: "rgba(0, 168, 132, 0.08)",
              },
            }}
          >
            Voltar
          </Button>
        </Stack>
      ) : (
        <IconButton size="small" onClick={onClose}>
          <Close />
        </IconButton>
      )}
    </Stack>
  );

  const content = (
    <Stack spacing={3} sx={{ width: "100%" }}>
      {/* Tabela de protocolos */}
      {isLoading ? (
        <Box
          sx={{
            p: 4,
            textAlign: "center",
            bgcolor: "#fafafa",
            borderRadius: 2,
          }}
        >
          <CircularProgress size={40} sx={{ color: "#00A884" }} />
          <Typography variant="body2" color="text.secondary" mt={2}>
            Carregando protocolos...
          </Typography>
        </Box>
      ) : templates.length === 0 ? (
        <Box
          sx={{
            p: 4,
            textAlign: "center",
            bgcolor: "#fafafa",
            border: "1px dashed #e0e0e0",
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Nenhum protocolo salvo
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Crie seu primeiro protocolo na tela de prescrições
          </Typography>
        </Box>
      ) : (
        <GenericTable
          tableColumns={tableColumns}
          tableRows={tableRows}
          actions={(row) => (
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Visualizar detalhes">
                <IconButton size="small" onClick={() => handleView(row)}>
                  <Visibility fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Editar protocolo">
                <IconButton size="small" onClick={() => handleStartEdit(row)}>
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Excluir protocolo">
                <IconButton
                  size="small"
                  onClick={() => handleOpenDeleteDialog(row)}
                  sx={{
                    color: "error.main",
                    "&:hover": {
                      bgcolor: "error.lighter",
                    },
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
          characterLimit={50}
        />
      )}

      {/* Preview do protocolo selecionado */}
      {viewingTemplate && (
        <Box
          sx={{
            p: 3,
            border: "2px solid #00A884",
            borderRadius: 2,
            bgcolor: "rgba(0, 168, 132, 0.02)",
          }}
        >
          <Stack spacing={2}>
            {/* Cabeçalho do preview */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <Typography variant="h6" fontWeight={600} color="#00A884">
                  {viewingTemplate.name}
                </Typography>
                {viewingTemplate.description && (
                  <Typography variant="body2" color="text.secondary" mt={0.5}>
                    {viewingTemplate.description}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mt={1}
                >
                  Criado {dayjs(viewingTemplate.createdAt).fromNow()}
                  {viewingTemplate.updatedAt !== viewingTemplate.createdAt &&
                    ` • Atualizado ${dayjs(
                      viewingTemplate.updatedAt,
                    ).fromNow()}`}
                </Typography>
              </Box>
              <Button
                size="small"
                startIcon={<Close />}
                onClick={() => setViewingTemplate(null)}
              >
                Fechar
              </Button>
            </Stack>

            {/* Conteúdo detalhado */}
            <Stack spacing={2}>
              {/* Tratamentos */}
              {viewingTemplate.treatments &&
                viewingTemplate.treatments.length > 0 && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      color="#00A884"
                      gutterBottom
                    >
                      💊 Tratamentos ({viewingTemplate.treatments.length})
                    </Typography>
                    <Stack spacing={1} pl={2}>
                      {viewingTemplate.treatments.map((t, idx) => (
                        <Box key={idx}>
                          <Typography variant="body2" fontWeight={600}>
                            • {t.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            pl={2}
                          >
                            {t.dosage}
                          </Typography>
                          {t.description && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              pl={2}
                            >
                              {t.description}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}

              {/* Exames */}
              {viewingTemplate.examsOrProcedures &&
                viewingTemplate.examsOrProcedures.length > 0 && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      color="#00A884"
                      gutterBottom
                    >
                      🔬 Exames/Procedimentos (
                      {viewingTemplate.examsOrProcedures.length})
                    </Typography>
                    <Stack spacing={1} pl={2}>
                      {viewingTemplate.examsOrProcedures.map((e, idx) => (
                        <Box key={idx}>
                          <Typography variant="body2" fontWeight={600}>
                            • {e.description}
                          </Typography>
                          {e.notes && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              pl={2}
                            >
                              {e.notes}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}

              {/* Relatórios */}
              {viewingTemplate.reports &&
                viewingTemplate.reports.length > 0 && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      color="#00A884"
                      gutterBottom
                    >
                      📄 Relatórios ({viewingTemplate.reports.length})
                    </Typography>
                    <Stack spacing={1} pl={2}>
                      {viewingTemplate.reports.map((r, idx) => (
                        <Typography
                          key={idx}
                          variant="body2"
                          sx={{ whiteSpace: "pre-wrap" }}
                        >
                          {r.content}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                )}

              {/* Orientações */}
              {viewingTemplate.guidelines && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    color="#00A884"
                    gutterBottom
                  >
                    📋 Orientações
                  </Typography>
                  <Typography
                    variant="body2"
                    pl={2}
                    sx={{ whiteSpace: "pre-wrap" }}
                  >
                    {viewingTemplate.guidelines}
                  </Typography>
                </Box>
              )}

              {/* Questionários do protocolo */}
              {viewingTemplate.questionnaires &&
                viewingTemplate.questionnaires.length > 0 && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      color="#00A884"
                      gutterBottom
                    >
                      📝 Questionários (
                      {viewingTemplate.questionnaires.length})
                    </Typography>
                    <Stack spacing={1} pl={2}>
                      {viewingTemplate.questionnaires.map((q) => (
                        <Typography key={q.questionnaireId} variant="body2">
                          • {q.title || q.name}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                )}
            </Stack>
          </Stack>
        </Box>
      )}
    </Stack>
  );

  if (asPage) {
    return (
      <Box sx={{ width: "100%", minWidth: 0 }}>
        {header}
        <Paper
          variant="outlined"
          sx={{ p: 3, borderRadius: 2, width: "100%", boxSizing: "border-box" }}
        >
          {content}
        </Paper>
        <TemplateEditor
          open={!!editingTemplate}
          onClose={() => setEditingTemplate(null)}
          template={editingTemplate}
          onSave={handleSaveEdit}
        />
        {deleteDialogOpen && (
          <Dialog
            open={deleteDialogOpen}
            onClose={handleCloseDeleteDialog}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="h6" fontWeight={600}>
                  Confirmar Exclusão
                </Typography>
                <IconButton size="small" onClick={handleCloseDeleteDialog}>
                  <Close />
                </IconButton>
              </Stack>
            </DialogTitle>
            <DialogContent>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Esta ação não pode ser desfeita.
              </Alert>
              <Typography variant="body1">
                Tem certeza que deseja excluir o protocolo{" "}
                <strong>{templateToDelete?.name}</strong>?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDeleteDialog} startIcon={<Close />}>
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmDelete}
                variant="contained"
                color="error"
                startIcon={<Delete />}
              >
                Excluir
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Box>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{header}</DialogTitle>
      <DialogContent dividers>{content}</DialogContent>
      <DialogActions>
        <Button onClick={onClose} startIcon={<Close />}>
          Fechar
        </Button>
      </DialogActions>
      {/* Modal de edição completa */}
      <TemplateEditor
        open={!!editingTemplate}
        onClose={() => setEditingTemplate(null)}
        template={editingTemplate}
        onSave={handleSaveEdit}
      />

      {/* Dialog de confirmação de exclusão */}

      {deleteDialogOpen && (
        <Dialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="h6" fontWeight={600}>
                Confirmar Exclusão
              </Typography>
              <IconButton size="small" onClick={handleCloseDeleteDialog}>
                <Close />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Esta ação não pode ser desfeita.
            </Alert>
            <Typography variant="body1">
              Tem certeza que deseja excluir o protocolo{" "}
              <strong>{templateToDelete?.name}</strong>?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog} startIcon={<Close />}>
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
              startIcon={<Delete />}
            >
              Excluir
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Dialog>
  );
};

export default TemplateManager;
